import React, { useState, useEffect } from 'react';
import { useSecureAuthContext } from '@/contexts/SecureAuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Lock, Eye, EyeOff, Shield, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';

const ChangePassword = () => {
  const { user } = useSecureAuthContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [isValidResetSession, setIsValidResetSession] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  
  // Password visibility states  
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Password validation states
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
    match: false
  });

  useEffect(() => {
    // Check if this is a valid password reset session
    const checkResetSession = async () => {
      try {
        setCheckingSession(true);
        
        // Check if user came from a reset email link with tokens
        const accessToken = searchParams.get('access_token');
        const refreshToken = searchParams.get('refresh_token');
        const type = searchParams.get('type');
        
        // First check for Supabase recovery tokens
        if (type === 'recovery' && accessToken && refreshToken) {
          // This is a valid reset session from Supabase, set the session
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });
          
          if (error) {
            console.error('Error setting reset session:', error);
            toast.error('Invalid or expired reset link. Please request a new password reset.');
            navigate('/auth');
            return;
          }
          
          setIsValidResetSession(true);
          console.log('Valid password reset session established via tokens');
        } else {
          // Check if user is already authenticated (for direct access)
          const { data: { session } } = await supabase.auth.getSession();
          
          if (session) {
            setIsValidResetSession(true);
            console.log('User is already authenticated, allowing password change');
          } else {
            // Not authenticated and no reset tokens, redirect to auth
            toast.error('This page can only be accessed via a password reset email link or when logged in.');
            navigate('/auth');
            return;
          }
        }
      } catch (error) {
        console.error('Error checking reset session:', error);
        toast.error('Invalid reset link. Please request a new password reset.');
        navigate('/auth');
      } finally {
        setCheckingSession(false);
      }
    };

    checkResetSession();
  }, [navigate, searchParams]);

  // Validate password requirements
  useEffect(() => {
    const hasLength = newPassword.length >= 8;
    const hasUppercase = /[A-Z]/.test(newPassword);
    const hasLowercase = /[a-z]/.test(newPassword);
    const hasNumber = /\d/.test(newPassword);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);
    const passwordsMatch = newPassword === confirmPassword && newPassword.length > 0;

    setPasswordValidation({
      length: hasLength,
      uppercase: hasUppercase,
      lowercase: hasLowercase,
      number: hasNumber,
      special: hasSpecial,
      match: passwordsMatch
    });
  }, [newPassword, confirmPassword]);

  const isPasswordValid = Object.values(passwordValidation).every(Boolean) && newPassword.length > 0;

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isPasswordValid) {
      toast.error('Please ensure all password requirements are met');
      return;
    }

    if (!isValidResetSession) {
      toast.error('Invalid session. Please use the reset link from your email.');
      return;
    }

    setLoading(true);
    
    try {
      console.log('=== PASSWORD RESET PROCESS STARTED ===');
      console.log('User ID:', user?.id);
      console.log('User Email:', user?.email);
      
      // Update the password using Supabase auth
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) {
        console.error('Password update error:', updateError);
        
        // Handle specific error cases
        if (updateError.message.includes('weak_password')) {
          toast.error('Password is too weak. Please choose a stronger password.');
        } else if (updateError.message.includes('same_password')) {
          toast.error('New password must be different from your current password.');
        } else {
          toast.error(`Password update failed: ${updateError.message}`);
        }
        return;
      }

      console.log('Password updated successfully in Supabase Auth and Database');

      // Log the password change for security audit
      try {
        const { error: logError } = await supabase.rpc('log_security_event', {
          event_type: 'password_reset_completed',
          event_data: {
            user_id: user?.id,
            user_email: user?.email,
            timestamp: new Date().toISOString(),
            source: 'password_reset_email_link'
          }
        });
        
        if (logError) {
          console.warn('Failed to log password reset event:', logError);
        }
      } catch (logErr) {
        console.warn('Error logging password reset:', logErr);
      }

      // Clear form
      setNewPassword('');
      setConfirmPassword('');
      
      console.log('=== PASSWORD RESET COMPLETED SUCCESSFULLY ===');
      toast.success('Password reset successfully! Redirecting to dashboard...');
      
      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
      
    } catch (error: any) {
      console.error('Unexpected error during password reset:', error);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const ValidationIndicator = ({ isValid, text }: { isValid: boolean; text: string }) => (
    <div className={`flex items-center text-sm ${isValid ? 'text-green-400' : 'text-gray-400'}`}>
      {isValid ? (
        <CheckCircle className="w-4 h-4 mr-2" />
      ) : (
        <AlertCircle className="w-4 h-4 mr-2" />
      )}
      {text}
    </div>
  );

  if (checkingSession) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-lg">Verifying reset session...</p>
        </div>
      </div>
    );
  }

  if (!isValidResetSession) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-white text-lg">Invalid reset session. Redirecting...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Grid Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-transparent to-purple-600/10"></div>
      </div>

      {/* Floating orbs */}
      <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse"></div>

      <header className="bg-black/20 backdrop-blur-md border-b border-white/20 relative z-10">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/auth" className="flex items-center text-white hover:text-blue-400 transition-colors">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Login
              </Link>
            </div>
            
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">BG</span>
              </div>
              <span className="text-xl font-bold text-white">The Bulk Image Generator</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-12 relative z-10">
        <div className="max-w-2xl mx-auto">
          <div className="mb-8 text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Reset Your Password</h1>
            <p className="text-gray-300">Create a new secure password for your account</p>
          </div>

          <Card className="bg-white/10 backdrop-blur-md border-white/20">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Shield className="w-5 h-5 mr-2" />
                Password Reset
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordChange} className="space-y-6">
                {/* New Password */}
                <div className="space-y-2">
                  <Label htmlFor="newPassword" className="text-gray-300">New Password</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="bg-white/10 border-white/20 text-white pr-12"
                      placeholder="Enter your new password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                      {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-gray-300">Confirm New Password</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="bg-white/10 border-white/20 text-white pr-12"
                      placeholder="Confirm your new password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Password Requirements */}
                {newPassword && (
                  <div className="bg-black/20 p-4 rounded-lg border border-white/10">
                    <h3 className="text-white font-medium mb-3">Password Requirements</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <ValidationIndicator isValid={passwordValidation.length} text="At least 8 characters" />
                      <ValidationIndicator isValid={passwordValidation.uppercase} text="One uppercase letter" />
                      <ValidationIndicator isValid={passwordValidation.lowercase} text="One lowercase letter" />
                      <ValidationIndicator isValid={passwordValidation.number} text="One number" />
                      <ValidationIndicator isValid={passwordValidation.special} text="One special character" />
                      <ValidationIndicator isValid={passwordValidation.match} text="Passwords match" />
                    </div>
                  </div>
                )}

                {/* Security Notice */}
                <Alert className="bg-blue-500/10 border-blue-500/20">
                  <Shield className="h-4 w-4 text-blue-400" />
                  <AlertDescription className="text-blue-300">
                    Your password will be updated immediately. After successful reset, you'll be redirected to your dashboard.
                  </AlertDescription>
                </Alert>

                <div className="flex gap-4">
                  <Button
                    type="button"
                    onClick={() => navigate('/auth')}
                    variant="outline"
                    className="flex-1 bg-transparent border-white/20 text-white hover:bg-white/10"
                  >
                    Cancel
                  </Button>
                  
                  <Button
                    type="submit"
                    disabled={loading || !isPasswordValid}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                  >
                    {loading ? 'Resetting Password...' : 'Reset Password'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Additional Security Tips */}
          <Card className="mt-6 bg-white/5 backdrop-blur-md border-white/10">
            <CardContent className="pt-6">
              <h3 className="text-white font-medium mb-3">Security Tips</h3>
              <ul className="text-gray-300 text-sm space-y-2">
                <li>• Use a unique password that you don't use for other accounts</li>
                <li>• Consider using a password manager to generate and store strong passwords</li>
                <li>• Avoid using personal information like names or birthdays in your password</li>
                <li>• Change your password regularly and immediately if you suspect it's been compromised</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default ChangePassword;